import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { AttendButton, AttendCount, AttendeesModal, canAttend } from '../components/CalendarAttendance';
import { CapacityStatus } from '../components/CalendarTeams';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Download, 
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  User,
  X,
  Search,
  Filter,
  RefreshCw,
  Rss,
  Copy,
  Check,
  Printer,
  Upload,
  List as ListIcon
} from 'lucide-react';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  addDays,
  addWeeks,
  addMonths,
  subWeeks,
  subMonths,
  isSameDay,
  getWeek,
  parseISO,
  isToday
} from 'date-fns';
import { nb } from 'date-fns/locale';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Default event type colors and labels (will be overridden by organization settings)
const DEFAULT_EVENT_TYPES = {
  training: { label: 'Trening', color: 'bg-blue-500', textColor: 'text-blue-500', bgLight: 'bg-blue-500/20' },
  dugnad: { label: 'Dugnad', color: 'bg-yellow-500', textColor: 'text-yellow-500', bgLight: 'bg-yellow-500/20' },
  safety_course: { label: 'Sikkerhetskurs', color: 'bg-green-500', textColor: 'text-green-500', bgLight: 'bg-green-500/20' },
  annual_meeting: { label: 'Årsmøte', color: 'bg-purple-500', textColor: 'text-purple-500', bgLight: 'bg-purple-500/20' },
  competition: { label: 'Stevne', color: 'bg-red-500', textColor: 'text-red-500', bgLight: 'bg-red-500/20' },
  other: { label: 'Annet', color: 'bg-gray-500', textColor: 'text-gray-500', bgLight: 'bg-gray-500/20' }
};

// Helper to convert color to text and bg-light variants
const getColorVariants = (color, textColor, customColor, customTextColor) => {
  // Handle custom colors (hex values)
  if (customColor) {
    return {
      color: '',
      customBg: customColor,
      textColor: textColor || '',
      customText: customTextColor,
      bgLight: `${customColor}33` // Add 20% opacity for hex
    };
  }
  
  // Handle Tailwind color classes
  const colorName = color?.replace('bg-', '').replace('-500', '') || 'gray';
  return {
    color,
    textColor: textColor || `text-${colorName}-500`,
    bgLight: `${color}/20`
  };
};

const Calendar = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(user?.organization?.calendar_default_view || 'week'); // 'day', 'week', 'month', 'list'
  const [listPage, setListPage] = useState(1);
  const [listAutoPaged, setListAutoPaged] = useState(false);
  const LIST_PAGE_SIZE = 30;
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // Track if editing an event
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [members, setMembers] = useState([]);
  const [eventTypes, setEventTypes] = useState(DEFAULT_EVENT_TYPES);
  const [locations, setLocations] = useState([]);
  const [showCustomLocation, setShowCustomLocation] = useState(false);
  const [showRecurringActionDialog, setShowRecurringActionDialog] = useState(false);
  const [recurringActionType, setRecurringActionType] = useState(null); // 'edit' or 'delete'
  
  // Form state for creating/editing events
  const [eventForm, setEventForm] = useState({
    title: '',
    event_type: 'training',
    start_datetime: '',
    end_datetime: '',
    location: '',
    skyteleder_id: '',
    skyteleder_name: '',
    description: '',
    recurrence: 'none', // none, daily, weekly, monthly, yearly
    is_important: false,
    attendance_enabled: true
  });

  // Recurrence options
  const RECURRENCE_OPTIONS = [
    { value: 'none', label: 'Ingen gjentagelse' },
    { value: 'daily', label: 'Hver dag' },
    { value: 'every_2_days', label: 'Annenhver dag' },
    { value: 'every_3_days', label: 'Hver tredje dag' },
    { value: 'weekly', label: 'Hver uke' },
    { value: 'every_2_weeks', label: 'Annenhver uke' },
    { value: 'every_3_weeks', label: 'Hver tredje uke' },
    { value: 'monthly', label: 'Hver måned' },
    { value: 'every_2_months', label: 'Annenhver måned' },
    { value: 'every_3_months', label: 'Hver tredje måned' },
    { value: 'yearly', label: 'Hvert år' }
  ];

  const canManageEvents = user?.role === 'admin' || user?.role === 'org_admin' || user?.role === 'super_admin';
  const canViewAttendance = canManageEvents || user?.role === 'range_officer';
  const [attendanceEnabled, setAttendanceEnabled] = useState(false);
  const [capacityEnabled, setCapacityEnabled] = useState(false);
  const [attendancePublic, setAttendancePublic] = useState(true);
  const [attendeesFor, setAttendeesFor] = useState(null);

  const applyAttendance = (ev, patch) => {
    const same = (e) => e.attendance_event_id === ev.attendance_event_id && e.occurrence_date === ev.occurrence_date;
    setEvents(list => list.map(e => same(e) ? { ...e, ...patch } : e));
    setSelectedEvent(cur => (cur && same(cur) ? { ...cur, ...patch } : cur));
  };

  // Fetch events
  const fetchEvents = useCallback(async (silent = false) => {
    try {
      if (silent !== true) setLoading(true);
      const token = localStorage.getItem('token');
      
      // Calculate date range based on view
      let startDate, endDate;
      if (view === 'day') {
        startDate = startOfDay(currentDate);
        endDate = endOfDay(currentDate);
      } else if (view === 'week') {
        startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
        endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
      } else if (view === 'list') {
        // List view: fetch wide range (past 3 months + future 12 months)
        startDate = startOfDay(addDays(new Date(), -90));
        endDate = endOfDay(addDays(new Date(), 365));
      } else {
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
      }
      
      const params = new URLSearchParams({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      });
      
      if (filterType !== 'all') {
        params.append('event_type', filterType);
      }
      
      const response = await axios.get(`${API_BASE}/api/calendar/events?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEvents(response.data.events || []);
      setAttendanceEnabled(response.data.attendance_enabled !== false);
      setCapacityEnabled(!!response.data.capacity_enabled);
      setAttendancePublic(response.data.attendance_public !== false);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Kunne ikke hente kalenderdata');
    } finally {
      setLoading(false);
    }
  }, [currentDate, view, filterType]);

  // Fetch members for skyteleder dropdown (org_admin = skyteleder, admin = administrator)
  const fetchMembers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns array directly, filter to only org_admin (skyteleder) and admin
      const allUsers = Array.isArray(response.data) ? response.data : (response.data.users || []);
      const eligibleMembers = allUsers.filter(
        m => m.role === 'org_admin' || m.role === 'admin' || m.role === 'super_admin'
      );
      setMembers(eligibleMembers);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  }, []);

  // Fetch event types from organization settings
  const fetchEventTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/calendar/event-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const types = response.data.event_types || [];
      // Convert to object format with color variants
      const typesObj = {};
      types.forEach(type => {
        const variants = getColorVariants(type.color, type.textColor, type.customColor, type.customTextColor);
        typesObj[type.id] = {
          label: type.label,
          ...variants
        };
      });
      setEventTypes(typesObj);
    } catch (error) {
      console.error('Error fetching event types:', error);
      // Keep default types on error
    }
  }, []);

  // Fetch locations from QR codes / training locations
  const fetchLocations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/calendar/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchEventTypes();
    fetchLocations();
  }, [fetchEvents, fetchEventTypes, fetchLocations]);

  // Sanntid: oppdater påmeldinger/lag jevnlig og etter egne endringer
  useEffect(() => {
    const refresh = () => fetchEvents(true);
    window.addEventListener('calendarRefresh', refresh);
    const i = attendanceEnabled ? setInterval(refresh, 30000) : null;
    return () => { window.removeEventListener('calendarRefresh', refresh); if (i) clearInterval(i); };
  }, [fetchEvents, attendanceEnabled]);

  useEffect(() => {
    if (canManageEvents) {
      fetchMembers();
    }
  }, [canManageEvents, fetchMembers]);

  // Navigation functions
  const goToToday = () => {
    if (view !== 'list') setCurrentDate(new Date());
    setListAutoPaged(false);
  };
  
  const goToPrevious = () => {
    if (view === 'day') setCurrentDate(prev => addDays(prev, -1));
    else if (view === 'week') setCurrentDate(prev => subWeeks(prev, 1));
    else setCurrentDate(prev => subMonths(prev, 1));
  };
  
  const goToNext = () => {
    if (view === 'day') setCurrentDate(prev => addDays(prev, 1));
    else if (view === 'week') setCurrentDate(prev => addWeeks(prev, 1));
    else setCurrentDate(prev => addMonths(prev, 1));
  };

  // Norwegian month names for date parsing
  const norwegianMonths = {
    'januar': 0, 'jan': 0,
    'februar': 1, 'feb': 1,
    'mars': 2, 'mar': 2,
    'april': 3, 'apr': 3,
    'mai': 4,
    'juni': 5, 'jun': 5,
    'juli': 6, 'jul': 6,
    'august': 7, 'aug': 7,
    'september': 8, 'sep': 8, 'sept': 8,
    'oktober': 9, 'okt': 9,
    'november': 10, 'nov': 10,
    'desember': 11, 'des': 11
  };

  // Try to parse search input as date and navigate
  const tryNavigateToDate = (input, forceNavigate = false) => {
    if (!input || input.length < 2) return false;
    
    const trimmed = input.trim().toLowerCase();
    let targetDate = null;
    const currentYear = new Date().getFullYear();
    
    // Pattern 1: "DD.MM" or "DD.MM.YYYY" or "DD/MM" or "DD/MM/YYYY"
    const datePattern = /^(\d{1,2})[./](\d{1,2})(?:[./](\d{2,4}))?$/;
    const dateMatch = trimmed.match(datePattern);
    if (dateMatch) {
      const day = parseInt(dateMatch[1], 10);
      const month = parseInt(dateMatch[2], 10) - 1; // 0-indexed
      let year = dateMatch[3] ? parseInt(dateMatch[3], 10) : currentYear;
      if (year < 100) year += 2000; // Convert 25 to 2025
      
      if (month >= 0 && month <= 11 && day >= 1 && day <= 31) {
        targetDate = new Date(year, month, day);
      }
    }
    
    // Pattern 2: Month name (optionally with year): "mars", "mars 2025", "mars25"
    if (!targetDate) {
      const monthPattern = /^([a-zæøå]+)\s*(\d{2,4})?$/;
      const monthMatch = trimmed.match(monthPattern);
      if (monthMatch) {
        const monthName = monthMatch[1];
        const monthIndex = norwegianMonths[monthName];
        if (monthIndex !== undefined) {
          let year = monthMatch[2] ? parseInt(monthMatch[2], 10) : currentYear;
          if (year < 100) year += 2000;
          targetDate = new Date(year, monthIndex, 1);
        }
      }
    }
    
    // Pattern 3: Just year: "2025" or "2026"
    if (!targetDate) {
      const yearPattern = /^(20\d{2})$/;
      const yearMatch = trimmed.match(yearPattern);
      if (yearMatch) {
        const year = parseInt(yearMatch[1], 10);
        targetDate = new Date(year, 0, 1); // January 1st of that year
      }
    }
    
    // Navigate if we found a valid date (on Enter or when pattern is complete)
    if (targetDate && !isNaN(targetDate.getTime())) {
      // Only auto-navigate on complete patterns or when forced (Enter key)
      const isComplete = dateMatch || forceNavigate;
      if (isComplete) {
        setCurrentDate(targetDate);
        // Switch to appropriate view
        if (dateMatch && dateMatch[1]) {
          setView('day'); // Specific day, switch to day view
        } else {
          setView('month'); // Month/year, switch to month view
        }
        return true;
      }
    }
    
    return false;
  };

  // Create event
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Find skyteleder name if ID is selected
      let skytelederName = eventForm.skyteleder_name;
      if (eventForm.skyteleder_id) {
        const member = members.find(m => m.id === eventForm.skyteleder_id);
        if (member) skytelederName = member.full_name;
      }
      
      // Send datetime as local time string (without UTC conversion)
      // Backend will interpret this in the organization's timezone
      await axios.post(`${API_BASE}/api/calendar/events`, {
        ...eventForm,
        skyteleder_name: skytelederName,
        start_datetime: eventForm.start_datetime,  // Keep as local time string
        end_datetime: eventForm.end_datetime,      // Keep as local time string
        recurrence: eventForm.recurrence || 'none'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Aktivitet opprettet!');
      setShowCreateModal(false);
      setEventForm({
        title: '',
        event_type: 'training',
        start_datetime: '',
        end_datetime: '',
        location: '',
        skyteleder_id: '',
        skyteleder_name: '',
        description: '',
        recurrence: 'none',
        is_important: false,
        attendance_enabled: true
      });
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Kunne ikke opprette aktivitet');
    }
  };

  // Delete event — handles recurring events with edit_mode
  const handleDeleteEvent = async (eventId, editMode = 'all') => {
    const event = selectedEvent;
    const isRecurringInstance = event?.is_instance || (event?.recurrence && event?.recurrence !== 'none');
    
    // For non-recurring events or 'all' mode, ask confirmation
    if (!isRecurringInstance || editMode === 'all') {
      if (!window.confirm('Er du sikker på at du vil slette denne aktiviteten?')) return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const actualEventId = event?.parent_event_id || eventId;
      
      let url = `${API_BASE}/api/calendar/events/${actualEventId}?edit_mode=${editMode}`;
      if (editMode !== 'all' && event?.start_datetime) {
        const instanceDate = event.start_datetime.split('T')[0];
        url += `&instance_date=${instanceDate}`;
      }
      
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const messages = {
        'this_only': 'Enkeltdag slettet',
        'this_and_future': 'Denne og alle fremtidige slettet',
        'all': 'Aktivitet slettet'
      };
      toast.success(messages[editMode] || 'Aktivitet slettet');
      setSelectedEvent(null);
      setShowEventModal(false);
      setShowRecurringActionDialog(false);
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Kunne ikke slette aktivitet');
    }
  };

  // Handle edit/delete click on recurring event — show action dialog
  const handleRecurringAction = (actionType) => {
    setRecurringActionType(actionType);
    setShowRecurringActionDialog(true);
  };

  // Open edit modal with selected event data
  const openEditModal = (event, editMode = null) => {
    // Format datetime for input fields using LOCAL time components from backend
    // This avoids DST conversion issues
    const formatDateTimeForInput = (isoString, localHour, localMinute) => {
      if (!isoString) return '';
      const date = new Date(isoString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      // Use local_hour/minute from backend if available (DST-safe)
      // Otherwise fall back to browser-converted time
      const hours = localHour !== undefined ? String(localHour).padStart(2, '0') : String(date.getHours()).padStart(2, '0');
      const minutes = localMinute !== undefined ? String(localMinute).padStart(2, '0') : String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Check if location is a predefined one or custom
    const eventLocation = event.location || '';
    const isCustomLocation = eventLocation && !locations.some(loc => loc.name === eventLocation);
    setShowCustomLocation(isCustomLocation);

    setEventForm({
      title: event.title || '',
      event_type: event.event_type || 'training',
      start_datetime: formatDateTimeForInput(event.start_datetime, event.local_start_hour, event.local_start_minute),
      end_datetime: formatDateTimeForInput(event.end_datetime, event.local_end_hour, event.local_end_minute),
      location: eventLocation,
      skyteleder_id: event.skyteleder_id || '',
      skyteleder_name: event.skyteleder_name || '',
      description: event.description || '',
      recurrence: editMode === 'this_only' ? 'none' : (event.recurrence || 'none'),
      is_important: !!event.is_important,
      attendance_enabled: event.attendance_enabled !== false,
      capacity_enabled: event.capacity_enabled !== false,
      capacity_extra_spot: !!event.capacity_extra_spot,
      _editMode: editMode  // Store edit mode for use in handleUpdateEvent
    });
    setIsEditing(true);
    setShowEventModal(false);
    setShowRecurringActionDialog(false);
    setShowCreateModal(true);
  };

  // Update event
  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Find skyteleder name if ID is selected
      let skytelederName = eventForm.skyteleder_name;
      if (eventForm.skyteleder_id) {
        const member = members.find(m => m.id === eventForm.skyteleder_id);
        if (member) skytelederName = member.full_name;
      }
      
      // Build update payload
      // Send datetime as local time string (without UTC conversion)
      const editMode = eventForm._editMode;
      const { _editMode, ...formWithoutEditMode } = eventForm;
      
      const updatePayload = {
        ...formWithoutEditMode,
        skyteleder_name: skytelederName,
        start_datetime: eventForm.start_datetime,  // Keep as local time string
        end_datetime: eventForm.end_datetime,      // Keep as local time string
        recurrence: eventForm.recurrence || 'none'
      };
      
      // Include edit_mode for recurring event handling
      if (editMode) {
        updatePayload.edit_mode = editMode;
      }
      
      // For recurring events, include the instance_date
      if (selectedEvent.is_instance && selectedEvent.start_datetime) {
        updatePayload.instance_date = selectedEvent.start_datetime;
      }
      
      // Use parent_event_id for recurring instances, otherwise use event id
      const eventIdToUpdate = selectedEvent.parent_event_id || selectedEvent.id;
      
      await axios.put(`${API_BASE}/api/calendar/events/${eventIdToUpdate}`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Aktivitet oppdatert!');
      setShowCreateModal(false);
      setIsEditing(false);
      setSelectedEvent(null);
      setEventForm({
        title: '',
        event_type: 'training',
        start_datetime: '',
        end_datetime: '',
        location: '',
        skyteleder_id: '',
        skyteleder_name: '',
        description: '',
        recurrence: 'none',
        is_important: false,
        attendance_enabled: true
      });
      fetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Kunne ikke oppdatere aktivitet');
    }
  };

  // Download iCal (one-time download)
  const downloadIcal = () => {
    if (!user?.organization_id) return;
    window.open(`${API_BASE}/api/calendar/ical/${user.organization_id}`, '_blank');
    toast.success('Kalender lastes ned...');
  };

  // Calendar subscription modal state
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [copiedSubscribe, setCopiedSubscribe] = useState(false);

  // Build URLs for live calendar subscription (dynamic - reflects all changes)
  const getSubscribeUrls = () => {
    if (!user?.organization_id) return null;
    const httpsUrl = `${API_BASE}/api/calendar/ical/${user.organization_id}`;
    const webcalUrl = httpsUrl.replace(/^https?:/, 'webcal:');
    return {
      https: httpsUrl,
      webcal: webcalUrl,
      // Google Calendar krever webcal://-schema via /render?cid=... — /r?cid=https:// gir "Sjekk nettadressen"
      google: `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcalUrl)}`,
      // Outlook: bruk webcal:// for live subscription (samme som time.ly)
      outlookWeb: webcalUrl,
    };
  };

  const copySubscribeLink = async (urlToCopy) => {
    try {
      await navigator.clipboard.writeText(urlToCopy);
      setCopiedSubscribe(true);
      setTimeout(() => setCopiedSubscribe(false), 2500);
      toast.success('Lenke kopiert til utklippstavlen');
    } catch (e) {
      toast.error('Kunne ikke kopiere automatisk. Marker og kopier manuelt.');
    }
  };

  // Filter events by search
  const filteredEvents = events.filter(event => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      event.title?.toLowerCase().includes(query) ||
      event.location?.toLowerCase().includes(query) ||
      event.skyteleder_name?.toLowerCase().includes(query) ||
      event.description?.toLowerCase().includes(query)
    );
  });

  // Get week days for week view
  const getWeekDays = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  // Get events for a specific day (deduplicated by instance_id or title+time combination)
  const getEventsForDay = (date) => {
    const dayEvents = filteredEvents.filter(event => {
      const eventDate = parseISO(event.start_datetime);
      return isSameDay(eventDate, date);
    });
    
    // Deduplicate events - prefer instance_id, fallback to title+time combination
    const seen = new Set();
    return dayEvents.filter(event => {
      // Create unique key: instance_id (for recurring) or id (for single events)
      const uniqueKey = event.instance_id || event.id;
      if (seen.has(uniqueKey)) {
        return false;
      }
      seen.add(uniqueKey);
      return true;
    });
  };

  // Helper to format time from event (uses local_start_hour/minute if available, fallback to parsed datetime)
  const formatEventTime = (event, type = 'start') => {
    if (type === 'start' && event.local_start_hour !== undefined) {
      return `${String(event.local_start_hour).padStart(2, '0')}:${String(event.local_start_minute || 0).padStart(2, '0')}`;
    }
    if (type === 'end' && event.local_end_hour !== undefined) {
      return `${String(event.local_end_hour).padStart(2, '0')}:${String(event.local_end_minute || 0).padStart(2, '0')}`;
    }
    // Fallback: parse from ISO string (may have DST issues for old events)
    const dt = type === 'start' ? event.start_datetime : event.end_datetime;
    return dt ? format(parseISO(dt), 'HH:mm') : '';
  };

  // Render event card
  const renderEventCard = (event, compact = false) => {
    const eventType = eventTypes[event.event_type] || eventTypes.other || DEFAULT_EVENT_TYPES.other;
    const startTime = formatEventTime(event, 'start');
    const endTime = formatEventTime(event, 'end');
    const isImportant = !!event.is_important;

    // VIKTIG-styling: tykkere ramme med ring rundt + rødlig farge
    const importantClasses = isImportant
      ? 'ring-2 ring-red-500/70 border-l-[6px] border-red-500 shadow-md shadow-red-500/20'
      : `border-l-4 ${eventType.color.replace('bg-', 'border-')}`;

    return (
      <div
        key={event.id}
        onClick={() => {
          setSelectedEvent(event);
          setShowEventModal(true);
        }}
        className={`${eventType.bgLight} ${importantClasses} rounded-r-lg p-2 mb-2 cursor-pointer hover:opacity-80 transition-opacity`}
        data-testid={isImportant ? 'calendar-event-card-important' : 'calendar-event-card'}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={`font-medium text-sm text-white truncate`}>{event.title}</p>
              {isImportant && (
                <span
                  className="text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold tracking-wide whitespace-nowrap"
                  data-testid="important-tag"
                >
                  VIKTIG
                </span>
              )}
            </div>
            {/* Beskrivelse rett under tittel */}
            {!compact && event.description && (
              <p className="text-xs text-gray-300 mt-1 whitespace-pre-wrap break-words line-clamp-2">
                {event.description}
              </p>
            )}
            {!compact && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 mt-1">
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <Clock className="w-3 h-3" />
                  <span className="font-semibold text-white">{startTime} - {endTime}</span>
                </span>
                {event.location && (
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <MapPin className="w-3 h-3" />
                    {event.location}
                  </span>
                )}
                {event.skyteleder_name && (
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <User className="w-3 h-3" />
                    {event.skyteleder_name}
                  </span>
                )}
              </div>
            )}
            {!compact && attendanceEnabled && canAttend(event) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2" data-testid="event-card-attendance">
                <AttendButton event={event} onChanged={applyAttendance} size="sm" />
                <AttendCount event={event} canView={canViewAttendance || attendancePublic} onOpen={setAttendeesFor} />
                <CapacityStatus event={event} />
              </div>
            )}
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${eventType.color} text-white whitespace-nowrap`}>
            {compact ? eventType.label.substring(0, 3) : eventType.label}
          </span>
        </div>
      </div>
    );
  };

  // Open create modal with a specific date pre-filled
  const openCreateModalForDate = (date) => {
    if (!canManageEvents) return;
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const defaultStart = `${year}-${month}-${day}T18:00`;
    const defaultEnd = `${year}-${month}-${day}T20:00`;
    
    setEventForm({
      title: '',
      event_type: 'training',
      start_datetime: defaultStart,
      end_datetime: defaultEnd,
      location: '',
      skyteleder_id: '',
      skyteleder_name: '',
      description: '',
      recurrence: 'none',
      is_important: false,
      attendance_enabled: true
    });
    setIsEditing(false);
    setShowCreateModal(true);
  };

  // Render week view (default, mobile-optimized)
  const renderWeekView = () => {
    const weekDays = getWeekDays();
    const weekNumber = getWeek(currentDate, { weekStartsOn: 1 });
    
    return (
      <div className="space-y-2">
        {/* Week header */}
        <div className="text-center mb-4 no-print">
          <span className="text-sm text-gray-400">Uke {weekNumber}</span>
        </div>
        
        {/* Days */}
        {weekDays.map((day) => {
          const dayEvents = getEventsForDay(day);
          const isCurrentDay = isToday(day);
          
          return (
            <div 
              key={day.toISOString()} 
              className={`rounded-lg p-3 ${isCurrentDay ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-gray-800/50'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${isCurrentDay ? 'text-yellow-400' : 'text-white'}`}>
                    {format(day, 'd')}
                  </span>
                  <span className="text-sm text-gray-400 capitalize">
                    {format(day, 'EEEE', { locale: nb })}
                  </span>
                </div>
                {isCurrentDay && (
                  <span className="text-xs bg-yellow-500 text-gray-900 px-2 py-0.5 rounded-full font-medium">
                    I dag
                  </span>
                )}
              </div>
              
              {dayEvents.length > 0 ? (
                <div className="space-y-2">
                  {dayEvents.map(event => renderEventCard(event))}
                </div>
              ) : (
                <div 
                  onClick={() => openCreateModalForDate(day)}
                  className={`text-sm text-gray-500 italic p-2 rounded-lg transition-colors ${canManageEvents ? 'cursor-pointer hover:bg-gray-700/50 hover:text-gray-400' : ''}`}
                  title={canManageEvents ? 'Klikk for å legge til aktivitet' : ''}
                >
                  {canManageEvents ? '+ Legg til aktivitet' : 'Ingen aktiviteter'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render month view
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    
    return (
      <div>
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map(d => (
            <div key={d} className="text-center text-xs text-gray-400 py-2">
              {d}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map(day => {
            const dayEvents = getEventsForDay(day);
            const isCurrentMonth = format(day, 'M') === format(currentDate, 'M');
            const isCurrentDay = isToday(day);
            
            return (
              <div
                key={day.toISOString()}
                onClick={(e) => {
                  // Only open create modal if clicking on empty space (not an event)
                  if (e.target === e.currentTarget || e.target.tagName === 'SPAN') {
                    if (dayEvents.length === 0 && canManageEvents) {
                      openCreateModalForDate(day);
                    }
                  }
                }}
                className={`min-h-[120px] p-1 rounded transition-colors ${
                  isCurrentDay 
                    ? 'bg-yellow-500/20 border border-yellow-500/50' 
                    : isCurrentMonth 
                      ? 'bg-gray-800/50' 
                      : 'bg-gray-900/30'
                } ${dayEvents.length === 0 && canManageEvents ? 'cursor-pointer hover:bg-gray-700/50' : ''}`}
                title={dayEvents.length === 0 && canManageEvents ? 'Klikk for å legge til aktivitet' : ''}
              >
                <span className={`text-xs ${isCurrentDay ? 'text-yellow-400 font-bold' : isCurrentMonth ? 'text-white' : 'text-gray-600'}`}>
                  {format(day, 'd')}
                </span>
                <div className="mt-1 space-y-1">
                  {dayEvents.slice(0, 3).map(event => {
                    const eventType = eventTypes[event.event_type] || eventTypes.other || DEFAULT_EVENT_TYPES.other;
                    return (
                      <div
                        key={event.id || event.instance_id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEvent(event);
                          setShowEventModal(true);
                        }}
                        className={`text-xs ${eventType.color} text-white px-1 py-0.5 rounded cursor-pointer hover:opacity-80`}
                      >
                        <div 
                          className="font-medium"
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: '1.2'
                          }}
                        >
                          {event.title}
                        </div>
                        {(event.location || event.skyteleder_name) && (
                          <div className="text-[10px] opacity-80 truncate mt-0.5">
                            {event.location && <span>{event.location}</span>}
                            {event.location && event.skyteleder_name && <span> • </span>}
                            {event.skyteleder_name && <span>{event.skyteleder_name}</span>}
                          </div>
                        )}
                        {attendanceEnabled && canAttend(event) && (
                          <div className="mt-1" data-testid="month-event-attendance">
                            <AttendButton event={event} onChanged={applyAttendance} size="xs" />
                            {event.attendance_count > 0 && (
                              <div className="text-[10px] opacity-90 mt-0.5 text-center" title="Antall som har meldt at de kommer.">{event.attendance_count} {event.attendance_count === 1 ? 'deltaker' : 'deltakere'}</div>
                            )}
                            {event.capacity && <div className="text-center leading-tight"><CapacityStatus event={event} compact /></div>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {dayEvents.length > 3 && (
                    <span className="text-xs text-gray-400">+{dayEvents.length - 3} mer</span>
                  )}
                  {dayEvents.length === 0 && canManageEvents && isCurrentMonth && (
                    <span className="text-[10px] text-gray-500 hover:text-gray-400">+ Legg til</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render day view
  const renderDayView = () => {
    const dayEvents = getEventsForDay(currentDate);
    
    return (
      <div className="space-y-3">
        <div className="text-center mb-4">
          <h3 className="text-xl font-bold text-white capitalize">
            {format(currentDate, 'EEEE dd.MM.yyyy', { locale: nb })}
          </h3>
          {isToday(currentDate) && (
            <span className="text-sm text-yellow-400">I dag</span>
          )}
        </div>
        
        {dayEvents.length > 0 ? (
          dayEvents.map(event => renderEventCard(event, false))
        ) : (
          <div 
            onClick={() => openCreateModalForDate(currentDate)}
            className={`text-center py-8 rounded-lg transition-colors ${canManageEvents ? 'cursor-pointer hover:bg-gray-800/50' : ''}`}
          >
            <CalendarIcon className="w-12 h-12 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-400">
              {canManageEvents ? 'Klikk for å legge til aktivitet' : 'Ingen aktiviteter denne dagen'}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Listevisning: start på siden med første kommende hendelse
  useEffect(() => {
    if (view !== 'list' || listAutoPaged || filteredEvents.length === 0) return;
    const sorted = [...filteredEvents].sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
    const now = new Date();
    const idx = sorted.findIndex(e => new Date(e.end_datetime || e.start_datetime) >= now);
    if (idx >= 0) setListPage(Math.floor(idx / LIST_PAGE_SIZE) + 1);
    setListAutoPaged(true);
    if (idx >= 0) {
      const key = format(parseISO(sorted[idx].start_datetime), 'yyyy-MM-dd');
      setTimeout(() => {
        const el = document.querySelector(`[data-list-date="${key}"]`) || [...document.querySelectorAll('[data-list-date]')].find(n => n.dataset.listDate >= key);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [view, events, listAutoPaged]);

  // Render list view (paginated, N per page)
  const renderListView = () => {
    // Get all events sorted chronologically (filteredEvents already applies search/filter)
    const allEvents = [...filteredEvents].sort((a, b) => {
      return new Date(a.start_datetime) - new Date(b.start_datetime);
    });

    const totalPages = Math.max(1, Math.ceil(allEvents.length / LIST_PAGE_SIZE));
    const safePage = Math.min(listPage, totalPages);
    const startIdx = (safePage - 1) * LIST_PAGE_SIZE;
    const pageEvents = allEvents.slice(startIdx, startIdx + LIST_PAGE_SIZE);

    // Group events by date (yyyy-mm-dd) for nicer presentation
    const byDate = {};
    pageEvents.forEach(ev => {
      const key = format(parseISO(ev.start_datetime), 'yyyy-MM-dd');
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(ev);
    });

    if (allEvents.length === 0) {
      return (
        <div className="text-center py-12">
          <CalendarIcon className="w-12 h-12 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400">Ingen aktiviteter funnet</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-sm text-gray-400 flex items-center justify-between no-print">
          <span data-testid="list-view-count">
            Viser {startIdx + 1}–{Math.min(startIdx + LIST_PAGE_SIZE, allEvents.length)} av {allEvents.length}
          </span>
        </div>

        {Object.entries(byDate).map(([dateKey, evs]) => {
          const dateObj = parseISO(dateKey);
          const isCurrentDay = isToday(dateObj);
          return (
            <div key={dateKey} data-list-date={dateKey} className={`rounded-lg p-3 scroll-mt-24 ${isCurrentDay ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-gray-800/50'}`}>
              <div className="flex items-baseline gap-2 mb-2">
                <h3 className="text-lg font-semibold text-white capitalize">
                  {format(dateObj, 'EEEE d. MMMM yyyy', { locale: nb })}
                </h3>
                {isCurrentDay && <span className="text-xs text-yellow-400">I dag</span>}
              </div>
              <div className="space-y-2">
                {evs.map(ev => renderEventCard(ev, false))}
              </div>
            </div>
          );
        })}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4 no-print" data-testid="list-pagination">
            <button
              onClick={() => setListPage(p => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-sm transition-colors"
              data-testid="list-prev-page"
            >
              ← Forrige
            </button>
            <span className="text-sm text-gray-300">
              Side {safePage} av {totalPages}
            </span>
            <button
              onClick={() => setListPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-sm transition-colors"
              data-testid="list-next-page"
            >
              Neste →
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white calendar-print-root">
      {/* Print-style: kun selve kalenderen skrives ut, i sort/hvitt */}
      <style>{`
        @media print {
          @page { margin: 1cm; size: A4 portrait; }
          /* Skjul alt på siden som ikke er kalenderen */
          body * { visibility: hidden !important; }
          .calendar-print-root, .calendar-print-root * { visibility: visible !important; }
          .calendar-print-root { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; background: #ffffff !important; color: #000000 !important; }
          /* Skjul knapper og navigasjon under print */
          .no-print, nav, header, footer, button[data-testid="subscribe-calendar-btn"], button[data-testid="print-calendar-btn"] { display: none !important; visibility: hidden !important; }
          /* Tving svart tekst og hvit bakgrunn på alle elementer */
          .calendar-print-root * {
            background: #ffffff !important;
            background-image: none !important;
            background-color: #ffffff !important;
            color: #000000 !important;
            box-shadow: none !important;
            text-shadow: none !important;
          }
          /* Beholder rammer rundt celler/kort, men i svart */
          .calendar-print-root [class*="border"] { border-color: #000000 !important; }
          /* Pills/badges får svart ramme i stedet for fyll */
          .calendar-print-root [class*="rounded-full"] {
            border: 1px solid #000000 !important;
            color: #000000 !important;
            font-weight: 600 !important;
          }
          /* Skjul scrollbars og dropdowns/modaler under print */
          [data-testid="subscribe-modal"], [data-testid="pause-modal"] { display: none !important; }
          /* Sørg for at innholdet fyller A4 */
          .calendar-print-root .max-w-7xl { max-width: 100% !important; }
        }
      `}</style>
      {/* Header */}
      <div className="sticky top-0 z-20 bg-gray-900/95 backdrop-blur border-b border-gray-700 px-4 py-3">
        <div className="max-w-4xl mx-auto">
          {/* Title row */}
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <CalendarIcon className="w-6 h-6 text-yellow-400" />
              Kalender
            </h1>
            <div className="flex items-center gap-2">
              {canManageEvents && (
                <button
                  onClick={() => {
                    // Set default times when opening create modal: 18:00 - 20:00
                    const today = new Date();
                    const year = today.getFullYear();
                    const month = String(today.getMonth() + 1).padStart(2, '0');
                    const day = String(today.getDate()).padStart(2, '0');
                    const defaultStart = `${year}-${month}-${day}T18:00`;
                    const defaultEnd = `${year}-${month}-${day}T20:00`;
                    
                    setEventForm({
                      title: '',
                      event_type: 'training',
                      start_datetime: defaultStart,
                      end_datetime: defaultEnd,
                      location: '',
                      skyteleder_id: '',
                      skyteleder_name: '',
                      description: '',
                      recurrence: 'none',
                      is_important: false,
                      attendance_enabled: true
                    });
                    setShowCreateModal(true);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500 text-gray-900 rounded-lg text-sm font-medium hover:bg-yellow-400 transition-colors no-print"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Legg til</span>
                </button>
              )}
              {canManageEvents && (
                <button
                  onClick={() => setShowImportModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600 transition-colors no-print"
                  title="Importér .ics-fil eller webcal://"
                  data-testid="import-calendar-btn"
                >
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Importér</span>
                </button>
              )}
              <button
                onClick={() => window.print()}
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600 transition-colors no-print"
                title="Skriv ut kalenderen (svart/hvitt)"
                data-testid="print-calendar-btn"
              >
                <Printer className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowSubscribeModal(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600 transition-colors no-print"
                title="Abonnér på kalenderen (live oppdateringer)"
                data-testid="subscribe-calendar-btn"
              >
                <Rss className="w-4 h-4" />
                <span className="hidden sm:inline">Abonnér</span>
              </button>
            </div>
          </div>
          
          {/* Navigation row */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 no-print">
              <button
                onClick={goToPrevious}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                I dag
              </button>
              <button
                onClick={goToNext}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            <div className="text-center flex-1">
              <h2 className="text-sm font-medium text-white capitalize">
                {view === 'day' && format(currentDate, 'dd.MM.yyyy', { locale: nb })}
                {view === 'week' && `Uke ${getWeek(currentDate, { weekStartsOn: 1 })} - ${format(currentDate, 'MMMM yyyy', { locale: nb })}`}
                {view === 'month' && format(currentDate, 'MMMM yyyy', { locale: nb })}
                {view === 'list' && 'Liste'}
              </h2>
            </div>
            
            {/* View selector */}
            <div className="flex items-center bg-gray-800 rounded-lg p-0.5 no-print">
              {[
                { key: 'day', label: 'Dag' },
                { key: 'week', label: 'Uke' },
                { key: 'month', label: 'Mnd' },
                { key: 'list', label: 'Liste' }
              ].map(v => (
                <button
                  key={v.key}
                  onClick={() => { setView(v.key); setListPage(1); setListAutoPaged(false); }}
                  data-testid={`view-${v.key}-btn`}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    view === v.key 
                      ? 'bg-yellow-500 text-gray-900 font-medium' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Search and filter row */}
          <div className="flex items-center gap-2 mt-3 no-print">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  
                  // Try to parse as date and navigate
                  // Supports formats: "15.03", "15.03.2025", "15/03", "15/03/2025", "mars", "mars 2025"
                  const dateNavigated = tryNavigateToDate(value);
                  if (!dateNavigated) {
                    // If not a valid date, it's a text search (handled by filteredEvents)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    // Force navigation on Enter
                    tryNavigateToDate(searchQuery, true);
                  }
                }}
                placeholder="Søk aktiviteter eller skriv dato (f.eks. 15.03, mars 2025)..."
                className="w-full pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <option value="all">Alle typer</option>
              {Object.entries(eventTypes).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Calendar content */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
          </div>
        ) : (
          <>
            {view === 'day' && renderDayView()}
            {view === 'week' && renderWeekView()}
            {view === 'month' && renderMonthView()}
            {view === 'list' && renderListView()}
          </>
        )}
        
        {/* Event type legend */}
        <div className="mt-6 p-4 bg-gray-800/50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-400 mb-2">Aktivitetstyper</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(eventTypes).map(([key, val]) => (
              <span 
                key={key} 
                className={`text-xs px-2 py-1 rounded ${val.color} text-white`}
              >
                {val.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Event Detail Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 p-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap pr-2 min-w-0">
                <h3 className="text-lg font-bold text-white truncate">{selectedEvent.title}</h3>
                {selectedEvent.is_important && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white font-bold tracking-wide whitespace-nowrap">
                    VIKTIG
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowEventModal(false)}
                className="p-1 hover:bg-gray-700 rounded-lg flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Event type badge */}
              <div>
                <span className={`text-sm px-3 py-1 rounded-full ${eventTypes[selectedEvent.event_type]?.color || 'bg-gray-500'} text-white`}>
                  {eventTypes[selectedEvent.event_type]?.label || 'Annet'}
                </span>
              </div>
              
              {/* Date & time */}
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium capitalize">
                    {format(parseISO(selectedEvent.start_datetime), 'EEEE dd.MM.yyyy', { locale: nb })}
                  </p>
                  <p className="text-gray-400">
                    {formatEventTime(selectedEvent, 'start')} - {formatEventTime(selectedEvent, 'end')}
                  </p>
                </div>
              </div>
              
              {/* Location */}
              {selectedEvent.location && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <p className="text-white">{selectedEvent.location}</p>
                </div>
              )}
              
              {/* Skyteleder */}
              {selectedEvent.skyteleder_name && (
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-400">Skyteleder</p>
                    <p className="text-white">{selectedEvent.skyteleder_name}</p>
                  </div>
                </div>
              )}
              
              {/* Recurrence */}
              {selectedEvent.recurrence && selectedEvent.recurrence !== 'none' && (
                <div className="flex items-start gap-3">
                  <RefreshCw className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-400">Gjentagelse</p>
                    <p className="text-white">
                      {RECURRENCE_OPTIONS.find(r => r.value === selectedEvent.recurrence)?.label || selectedEvent.recurrence}
                    </p>
                  </div>
                </div>
              )}
              
              {/* «Jeg kommer» */}
              {attendanceEnabled && canAttend(selectedEvent) && (
                <div className="pt-3 border-t border-gray-700 flex flex-wrap items-center gap-3" data-testid="event-modal-attendance">
                  <AttendButton event={selectedEvent} onChanged={applyAttendance} />
                  <AttendCount event={selectedEvent} canView={canViewAttendance || attendancePublic} onOpen={setAttendeesFor} />
                  <CapacityStatus event={selectedEvent} />
                </div>
              )}

              {/* Description */}
              {selectedEvent.description && (
                <div className="pt-2 border-t border-gray-700">
                  <p className="text-sm text-gray-400 mb-1">Beskrivelse</p>
                  <p className="text-white whitespace-pre-wrap">{selectedEvent.description}</p>
                </div>
              )}
              
              {/* Actions */}
              {canManageEvents && (
                <div className="pt-4 border-t border-gray-700 flex gap-2">
                  {(() => {
                    const isRecurring = selectedEvent.is_instance || (selectedEvent.recurrence && selectedEvent.recurrence !== 'none');
                    return (
                      <>
                        <button
                          onClick={() => isRecurring ? handleRecurringAction('edit') : openEditModal(selectedEvent)}
                          className="flex-1 px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                          data-testid="edit-event-btn"
                        >
                          Rediger
                        </button>
                        <button
                          onClick={() => isRecurring ? handleRecurringAction('delete') : handleDeleteEvent(selectedEvent.id)}
                          className="flex-1 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                          data-testid="delete-event-btn"
                        >
                          Slett
                        </button>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {attendeesFor && <AttendeesModal event={attendeesFor} canManage={canViewAttendance} onClose={() => { setAttendeesFor(null); fetchEvents(); }} />}

      {/* Recurring Event Action Dialog */}
      {showRecurringActionDialog && selectedEvent && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm">
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white">
                {recurringActionType === 'delete' ? 'Slett repeterende hendelse' : 'Rediger repeterende hendelse'}
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Denne hendelsen gjentar seg. Hva vil du {recurringActionType === 'delete' ? 'slette' : 'endre'}?
              </p>
            </div>
            <div className="p-4 space-y-2">
              <button
                onClick={() => {
                  if (recurringActionType === 'delete') {
                    handleDeleteEvent(selectedEvent.parent_event_id || selectedEvent.id, 'this_only');
                  } else {
                    openEditModal(selectedEvent, 'this_only');
                  }
                }}
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-left"
                data-testid="recurring-this-only-btn"
              >
                <span className="font-medium">Kun denne dagen</span>
                <p className="text-xs text-gray-400 mt-0.5">Andre dager i serien påvirkes ikke</p>
              </button>
              <button
                onClick={() => {
                  if (recurringActionType === 'delete') {
                    handleDeleteEvent(selectedEvent.parent_event_id || selectedEvent.id, 'this_and_future');
                  } else {
                    openEditModal(selectedEvent, 'this_and_future');
                  }
                }}
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-left"
                data-testid="recurring-this-and-future-btn"
              >
                <span className="font-medium">Denne og alle fremtidige</span>
                <p className="text-xs text-gray-400 mt-0.5">Hendelser før denne datoen beholdes</p>
              </button>
              <button
                onClick={() => {
                  if (recurringActionType === 'delete') {
                    handleDeleteEvent(selectedEvent.parent_event_id || selectedEvent.id, 'all');
                  } else {
                    openEditModal(selectedEvent, 'all');
                  }
                }}
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-left"
                data-testid="recurring-all-btn"
              >
                <span className="font-medium">Alle hendelser i serien</span>
                <p className="text-xs text-gray-400 mt-0.5">{recurringActionType === 'delete' ? 'Sletter hele den repeterende serien' : 'Endrer alle forekomster'}</p>
              </button>
            </div>
            <div className="p-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowRecurringActionDialog(false);
                  setRecurringActionType(null);
                }}
                className="w-full px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                data-testid="recurring-cancel-btn"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Event Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">{isEditing ? 'Rediger aktivitet' : 'Ny aktivitet'}</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setIsEditing(false);
                  setShowCustomLocation(false);
                  setEventForm({
                    title: '',
                    event_type: 'training',
                    start_datetime: '',
                    end_datetime: '',
                    location: '',
                    skyteleder_id: '',
                    skyteleder_name: '',
                    description: '',
                    recurrence: 'none',
                    is_important: false,
                    attendance_enabled: true
                  });
                }}
                className="p-1 hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={isEditing ? handleUpdateEvent : handleCreateEvent} className="p-4 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Tittel *</label>
                <input
                  type="text"
                  required
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="F.eks. Treningskveld"
                />
              </div>
              
              {/* Event type */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Type aktivitet *</label>
                <select
                  required
                  value={eventForm.event_type}
                  onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  {Object.entries(eventTypes).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Start datetime */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Starttid *</label>
                <input
                  type="datetime-local"
                  required
                  value={eventForm.start_datetime}
                  onChange={(e) => {
                    const newStartDateTime = e.target.value;
                    // Extract date part and set end datetime to same date with default end time (20:00)
                    const datePart = newStartDateTime.split('T')[0];
                    const startTime = newStartDateTime.split('T')[1] || '18:00';
                    
                    // If end_datetime is empty or has different date, update it
                    const currentEndDate = eventForm.end_datetime ? eventForm.end_datetime.split('T')[0] : '';
                    let newEndDateTime = eventForm.end_datetime;
                    
                    if (!eventForm.end_datetime || currentEndDate !== datePart) {
                      // Set end time to 20:00 by default, or 2 hours after start time
                      const startHour = parseInt(startTime.split(':')[0], 10);
                      const endHour = Math.min(startHour + 2, 23);
                      newEndDateTime = `${datePart}T${String(endHour).padStart(2, '0')}:00`;
                    }
                    
                    setEventForm({ 
                      ...eventForm, 
                      start_datetime: newStartDateTime,
                      end_datetime: newEndDateTime
                    });
                  }}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              
              {/* End datetime */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Sluttid *</label>
                <input
                  type="datetime-local"
                  required
                  value={eventForm.end_datetime}
                  onChange={(e) => setEventForm({ ...eventForm, end_datetime: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              
              {/* Recurrence */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Gjentagelse</label>
                <select
                  value={eventForm.recurrence}
                  onChange={(e) => setEventForm({ ...eventForm, recurrence: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  data-testid="recurrence-select"
                >
                  {RECURRENCE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Location */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Bane/sted</label>
                {!showCustomLocation ? (
                  <select
                    value={eventForm.location}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setShowCustomLocation(true);
                        setEventForm({ ...eventForm, location: '' });
                      } else {
                        setEventForm({ ...eventForm, location: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    data-testid="location-select"
                  >
                    <option value="">Velg bane/sted</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.name}>
                        {loc.name}
                      </option>
                    ))}
                    <option value="__custom__">--- Skriv eget sted ---</option>
                  </select>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={eventForm.location}
                      onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Skriv inn sted..."
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowCustomLocation(false);
                        setEventForm({ ...eventForm, location: '' });
                      }}
                      className="px-3 py-2 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500"
                      title="Velg fra liste"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Skyteleder - show for all event types */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Skyteleder</label>
                <select
                  value={eventForm.skyteleder_id}
                  onChange={(e) => {
                    const member = members.find(m => m.id === e.target.value);
                    setEventForm({ 
                      ...eventForm, 
                      skyteleder_id: e.target.value,
                      skyteleder_name: member?.full_name || ''
                    });
                  }}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  data-testid="skyteleder-select"
                >
                  <option value="">Velg skyteleder</option>
                  {[...members].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'nb')).map(member => (
                    <option key={member.id} value={member.id}>
                      {member.full_name} ({member.role === 'org_admin' ? 'Skyteleder' : member.role === 'admin' ? 'Admin' : 'Super Admin'})
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Beskrivelse</label>
                <textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
                  placeholder="Tilleggsinformasjon..."
                />
              </div>

              {/* «Jeg kommer» for denne aktiviteten */}
              {attendanceEnabled && (
                <div>
                  <label className="flex items-center gap-3 cursor-pointer select-none p-3 bg-gray-700/50 border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors">
                    <input
                      type="checkbox"
                      checked={eventForm.attendance_enabled !== false}
                      onChange={(e) => setEventForm({ ...eventForm, attendance_enabled: e.target.checked })}
                      className="w-5 h-5 accent-yellow-500 cursor-pointer"
                      data-testid="event-attendance-checkbox"
                    />
                    <div className="flex-1">
                      <span className="text-white font-medium text-sm">Vis «Jeg kommer»-knapp for denne aktiviteten</span>
                      <div className="text-xs text-gray-400 mt-0.5">
                        Medlemmer kan melde at de kommer, og antall deltakere vises på aktiviteten
                      </div>
                    </div>
                  </label>
                </div>
              )}

              {/* Kapasitet / lag (bane med standplasser) */}
              {attendanceEnabled && capacityEnabled && eventForm.attendance_enabled !== false && (() => {
                const loc = locations.find(l => l.name === eventForm.location);
                const pos = Number(loc?.shooting_positions || 0);
                if (!pos) return eventForm.location ? (
                  <p className="text-xs text-gray-400 px-1" data-testid="event-capacity-none">Banen har ikke satt antall standplasser – ingen lag for denne aktiviteten.</p>
                ) : null;
                const on = eventForm.capacity_enabled !== false;
                return (
                  <div className="p-3 bg-sky-500/10 border border-sky-500/30 rounded-lg space-y-2" data-testid="event-capacity-box">
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <input type="checkbox" checked={on} onChange={(e) => setEventForm({ ...eventForm, capacity_enabled: e.target.checked })}
                        className="w-5 h-5 accent-yellow-500 cursor-pointer" data-testid="event-capacity-checkbox" />
                      <span className="text-white font-medium text-sm flex-1">Kapasitet og lag: {pos + (on && eventForm.capacity_extra_spot ? 1 : 0)} plasser per lag</span>
                    </label>
                    {on && (
                      <label className="flex items-center gap-3 cursor-pointer select-none pl-8">
                        <input type="checkbox" checked={!!eventForm.capacity_extra_spot} onChange={(e) => setEventForm({ ...eventForm, capacity_extra_spot: e.target.checked })}
                          className="w-4 h-4 accent-yellow-500 cursor-pointer" data-testid="event-capacity-extra-checkbox" />
                        <span className="text-sm text-gray-200">+1 ekstra plass på denne treningen (f.eks. sidebord)</span>
                      </label>
                    )}
                    <p className="text-xs text-gray-400">{pos} standplasser hentet fra banen «{eventForm.location}». Medlemmene får lag etter hvem som melder seg på først.</p>
                  </div>
                );
              })()}

              {/* Marker som VIKTIG */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer select-none p-3 bg-gray-700/50 border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors">
                  <input
                    type="checkbox"
                    checked={!!eventForm.is_important}
                    onChange={(e) => setEventForm({ ...eventForm, is_important: e.target.checked })}
                    className="w-5 h-5 accent-red-500 cursor-pointer"
                    data-testid="event-important-checkbox"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium text-sm">Marker som VIKTIG</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold tracking-wide">
                        VIKTIG
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      Hendelsen fremheves med rød ramme og VIKTIG-merke i kalenderen
                    </div>
                  </div>
                </label>
              </div>
              
              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full px-4 py-3 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
                  data-testid="submit-event-btn"
                >
                  {isEditing ? 'Lagre endringer' : 'Opprett aktivitet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Calendar Subscribe Modal - Live abonnement (webcal://) */}
      {showSubscribeModal && (() => {
        const urls = getSubscribeUrls();
        if (!urls) return null;
        return (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="subscribe-modal">
            <div className="bg-gray-800 border border-yellow-500/40 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl shadow-yellow-500/10">
              {/* Header */}
              <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-yellow-500/20 p-2 rounded-full">
                    <Rss className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Abonnér på kalender</h3>
                    <p className="text-xs text-gray-400">Velg din kalender-app for live oppdateringer</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSubscribeModal(false)}
                  className="text-gray-400 hover:text-white p-1"
                  aria-label="Lukk"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-2">
                {/* Info banner */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-3">
                  <p className="text-xs text-yellow-200">
                    <strong>Live oppdateringer:</strong> Endringer i klubbens kalender vises automatisk i din lokale kalender innen kort tid.
                  </p>
                </div>

                {/* Google Calendar */}
                <a
                  href={urls.google}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 w-full p-3 rounded-lg bg-gray-700 hover:bg-gray-600 border border-gray-600 hover:border-yellow-500 transition-colors group"
                  data-testid="subscribe-google"
                >
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" className="w-6 h-6"><path fill="#4285F4" d="M5 5h14v14H5z" opacity=".1"/><text x="50%" y="55%" textAnchor="middle" dominantBaseline="middle" fontFamily="Arial,sans-serif" fontWeight="bold" fontSize="11" fill="#4285F4">31</text></svg>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-white font-medium text-sm">Legg til i Google Kalender</div>
                    <div className="text-xs text-gray-400">Åpnes i nettleseren</div>
                  </div>
                  <span className="text-yellow-400 text-xs group-hover:translate-x-1 transition-transform">→</span>
                </a>

                {/* Apple Calendar */}
                <a
                  href={urls.webcal}
                  className="flex items-center gap-3 w-full p-3 rounded-lg bg-gray-700 hover:bg-gray-600 border border-gray-600 hover:border-yellow-500 transition-colors group"
                  data-testid="subscribe-apple"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="white"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-white font-medium text-sm">Legg til i Apple Kalender</div>
                    <div className="text-xs text-gray-400">Åpner Apple Kalender-appen</div>
                  </div>
                  <span className="text-yellow-400 text-xs group-hover:translate-x-1 transition-transform">→</span>
                </a>

                {/* Outlook */}
                <a
                  href={urls.outlookWeb}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 w-full p-3 rounded-lg bg-gray-700 hover:bg-gray-600 border border-gray-600 hover:border-yellow-500 transition-colors group"
                  data-testid="subscribe-outlook"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#0078D4] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">O</span>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-white font-medium text-sm">Legg til i Outlook</div>
                    <div className="text-xs text-gray-400">Outlook web / Microsoft 365</div>
                  </div>
                  <span className="text-yellow-400 text-xs group-hover:translate-x-1 transition-transform">→</span>
                </a>

                {/* Other / Manual URL */}
                <div className="border border-gray-600 rounded-lg p-3 bg-gray-700/50">
                  <div className="flex items-center gap-3 mb-2">
                    <CalendarIcon className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-white font-medium text-sm">Annen kalender-app</div>
                      <div className="text-xs text-gray-400">Kopier webcal-lenken og lim den inn i appen din</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-gray-900 rounded p-2 border border-gray-700">
                    <code className="flex-1 text-xs text-yellow-200 truncate" data-testid="subscribe-webcal-url">
                      {urls.webcal}
                    </code>
                    <button
                      onClick={() => copySubscribeLink(urls.webcal)}
                      className="px-2 py-1 bg-yellow-500 hover:bg-yellow-400 text-gray-900 text-xs font-medium rounded flex items-center gap-1 flex-shrink-0"
                      data-testid="subscribe-copy-btn"
                    >
                      {copiedSubscribe ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copiedSubscribe ? 'Kopiert!' : 'Kopier'}
                    </button>
                  </div>
                </div>

                {/* Last ned engangs-fil */}
                <button
                  onClick={() => { downloadIcal(); setShowSubscribeModal(false); }}
                  className="flex items-center gap-3 w-full p-3 rounded-lg bg-gray-700/40 hover:bg-gray-700 border border-gray-700 transition-colors group"
                  data-testid="subscribe-download-ics"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-600 flex items-center justify-center flex-shrink-0">
                    <Download className="w-5 h-5 text-gray-300" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-white font-medium text-sm">Last ned som .ics-fil</div>
                    <div className="text-xs text-gray-400">Statisk engangs-fil (oppdateres ikke automatisk)</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Import Modal */}
      {showImportModal && (
        <ImportCalendarModal
          onClose={() => setShowImportModal(false)}
          onImported={() => { setShowImportModal(false); fetchEvents(); toast.success('Hendelser importert'); }}
        />
      )}

    </div>
  );
};

// ============== Import Calendar Modal ==============
const ImportCalendarModal = ({ onClose, onImported }) => {
  const [mode, setMode] = useState('file'); // 'file' | 'url'
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('input'); // 'input' | 'preview'
  const [previewEvents, setPreviewEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  const handlePreview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      if (mode === 'file') {
        if (!file) { toast.error('Velg en .ics-fil'); setLoading(false); return; }
        formData.append('file', file);
      } else {
        if (!url.trim()) { toast.error('Skriv inn en URL'); setLoading(false); return; }
        formData.append('url', url.trim());
      }
      const res = await axios.post(`${API_BASE}/api/calendar/import/preview`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const items = res.data.events || [];
      setPreviewEvents(items);
      // Pre-select all non-duplicates
      const ids = new Set(items.filter(e => !e.is_duplicate).map((_, i) => i));
      setSelectedIds(ids);
      setStep('preview');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Kunne ikke lese kalender');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (idx) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === previewEvents.length) {
      setSelectedIds(new Set());
    } else {
      const indices = previewEvents
        .map((e, i) => (skipDuplicates && e.is_duplicate) ? null : i)
        .filter(i => i !== null);
      setSelectedIds(new Set(indices));
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const eventsToImport = previewEvents.filter((_, idx) => selectedIds.has(idx));
      const res = await axios.post(`${API_BASE}/api/calendar/import/confirm`,
        { events: eventsToImport, event_type: 'training' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Importerte ${res.data.inserted} aktiviteter`);
      onImported();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Import feilet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-testid="import-modal">
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 p-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Upload className="w-5 h-5 text-yellow-400" />
            Importér kalender
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="import-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {step === 'input' && (
            <>
              <p className="text-sm text-gray-400">
                Importér aktiviteter fra en .ics-fil eller en webcal://-URL.
                Alle importerte aktiviteter blir satt til typen <strong>Trening</strong>. Du kan endre type på de få som ikke er trening etterpå.
              </p>

              <div className="flex gap-2 border-b border-gray-700">
                <button
                  onClick={() => setMode('file')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === 'file' ? 'border-yellow-500 text-yellow-400' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
                  data-testid="import-mode-file"
                >
                  Last opp .ics-fil
                </button>
                <button
                  onClick={() => setMode('url')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === 'url' ? 'border-yellow-500 text-yellow-400' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
                  data-testid="import-mode-url"
                >
                  Fra URL (webcal://)
                </button>
              </div>

              {mode === 'file' ? (
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Velg .ics-fil</label>
                  <input
                    type="file"
                    accept=".ics,text/calendar"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-yellow-500 file:text-gray-900 file:font-medium hover:file:bg-yellow-400"
                    data-testid="import-file-input"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Kalender-URL</label>
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="webcal://example.com/calendar.ics eller https://..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    data-testid="import-url-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Webcal-URL hentes én gang (snapshot). Den synkroniseres ikke automatisk i ettertid.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg" data-testid="import-cancel-btn">
                  Avbryt
                </button>
                <button
                  onClick={handlePreview}
                  disabled={loading}
                  className="px-4 py-2 text-sm bg-yellow-500 text-gray-900 font-medium rounded-lg hover:bg-yellow-400 disabled:opacity-50"
                  data-testid="import-preview-btn"
                >
                  {loading ? 'Leser...' : 'Vis forhåndsvisning'}
                </button>
              </div>
            </>
          )}

          {step === 'preview' && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-gray-300">
                  Fant <strong>{previewEvents.length}</strong> aktiviteter.
                  Velg hvilke du vil importere:
                </p>
                <label className="flex items-center gap-2 text-xs text-gray-400">
                  <input
                    type="checkbox"
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                    data-testid="import-skip-duplicates"
                  />
                  Hopp over duplikater
                </label>
              </div>

              <div className="flex justify-between items-center">
                <button onClick={toggleAll} className="text-xs text-yellow-400 hover:text-yellow-300" data-testid="import-toggle-all">
                  {selectedIds.size === previewEvents.length ? 'Fjern alle' : 'Velg alle'}
                </button>
                <span className="text-xs text-gray-400">{selectedIds.size} valgt</span>
              </div>

              <div className="max-h-96 overflow-y-auto border border-gray-700 rounded-lg divide-y divide-gray-700">
                {previewEvents.map((ev, idx) => (
                  <label
                    key={idx}
                    className={`flex items-start gap-3 p-3 hover:bg-gray-700/40 cursor-pointer ${ev.is_duplicate ? 'opacity-60' : ''}`}
                    data-testid={`import-preview-row-${idx}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(idx)}
                      onChange={() => toggleSelect(idx)}
                      disabled={skipDuplicates && ev.is_duplicate}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white truncate">{ev.title}</span>
                        {ev.is_duplicate && (
                          <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-300 rounded-full">Duplikat</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {format(parseISO(ev.start_datetime), 'EEEE d. MMM yyyy HH:mm', { locale: nb })}
                        {ev.location ? ` · ${ev.location}` : ''}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex justify-between gap-2 pt-2">
                <button
                  onClick={() => setStep('input')}
                  className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg"
                  data-testid="import-back-btn"
                >
                  ← Tilbake
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={loading || selectedIds.size === 0}
                  className="px-4 py-2 text-sm bg-yellow-500 text-gray-900 font-medium rounded-lg hover:bg-yellow-400 disabled:opacity-50"
                  data-testid="import-confirm-btn"
                >
                  {loading ? 'Importerer...' : `Importér ${selectedIds.size} hendelser`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
